import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Loading from '../../components/common/Loading';
import Alert from '../../components/common/Alert';
import Badge from '../../components/common/Badge';
import { partnerService } from '../../services/partnerService';
import { purchaseService } from '../../services/purchaseService';
import { Partner, PurchaseValidation, CreatePurchaseData } from '../../types';
import { formatCNPJ, formatPercentage, formatDateTime, formatCPF, getImageUrl } from '../../utils/formatters';
const QRCode = require('qrcode.react');

type Step = 'select-partner' | 'validate' | 'qrcode' | 'success';

const PurchaseFlow: React.FC = () => {
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState<Step>('select-partner');
  const [partners, setPartners] = useState<Partner[]>([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(null);
  const [associateId, setAssociateId] = useState('');
  const [validation, setValidation] = useState<PurchaseValidation | null>(null);
  const [purchaseCode, setPurchaseCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadPartners();
  }, []);

  const loadPartners = async () => {
    try {
      setLoading(true);
      const data = await partnerService.getAll(1, '');
      setPartners(data.data.filter((p) => p.ativo));
    } catch (err: any) {
      setError('Erro ao carregar parceiros');
    } finally {
      setLoading(false);
    }
  };

  const handlePartnerSelect = (partnerId: number) => {
    setSelectedPartnerId(partnerId);
    setCurrentStep('validate');
  };

  const handleValidate = async () => {
    if (!associateId || !selectedPartnerId) {
      setError('Preencha o ID do associado');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const validationData = await purchaseService.validatePurchase(
        Number(associateId),
        selectedPartnerId
      );

      if (!validationData.apto) {
        setError(validationData.motivo || 'Associado n�o est� apto para utilizar o benef�cio');
        setLoading(false);
        return;
      }

      setValidation(validationData);
      setCurrentStep('qrcode');
    } catch (err: any) {
      setError(err.message || 'Erro ao validar associado');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!associateId || !selectedPartnerId) return;

    setLoading(true);
    setError('');

    try {
      const purchaseData: CreatePurchaseData = {
        associado_id: Number(associateId),
        parceiro_id: selectedPartnerId,
      };

      const purchase = await purchaseService.create(purchaseData);
      if (purchase.data) { setPurchaseCode(purchase.data.codigo_validacao); }
      setCurrentStep('success');
    } catch (err: any) {
      setError(err.message || 'Erro ao confirmar utiliza��o');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setCurrentStep('select-partner');
    setSelectedPartnerId(null);
    setAssociateId('');
    setValidation(null);
    setPurchaseCode('');
    setError('');
  };

  const renderSelectPartner = () => (
    <>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Selecione o Parceiro</h2>
        <p className="text-sm text-gray-600 mt-1">
          Escolha o estabelecimento onde o benef�cio ser� utilizado
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loading size="lg" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {partners.map((partner) => (
            <Card
              key={partner.id}
              className="hover:shadow-lg transition-all cursor-pointer hover:border-blue-500"
              onClick={() => handlePartnerSelect(Number(partner.id))}
            >
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  {partner.logo_url ? (
                    <img
                      src={getImageUrl(partner.logo_url) || ''}
                      alt={partner.nome_fantasia || ''}
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-lg">
                        {partner.nome_fantasia && partner.nome_fantasia.charAt(0)}
                      </span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {partner.nome_fantasia}
                    </h3>
                    <p className="text-xs text-gray-500 truncate">
                      {formatCNPJ(partner.cnpj)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-2 border-t">
                  <span className="text-sm text-gray-600">Desconto</span>
                  <span className="text-lg font-bold text-green-600">
                    {formatPercentage(partner.desconto_percentual || 0)}
                  </span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </>
  );

  const renderValidate = () => {
    const selectedPartner = partners.find((p) => p.id === String(selectedPartnerId));

    return (
      <>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Validar Associado</h2>
          <p className="text-sm text-gray-600 mt-1">
            Informe o ID do associado para validar o benef�cio
          </p>
        </div>

        {selectedPartner && (
          <Card className="mb-6 bg-blue-50 border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-700">Parceiro selecionado</p>
                <p className="text-lg font-semibold text-gray-900">
                  {selectedPartner.nome_fantasia}
                </p>
              </div>
              <Badge variant="success">
                {formatPercentage(selectedPartner.desconto_percentual || 0)}
              </Badge>
            </div>
          </Card>
        )}

        <Card>
          <div className="space-y-4">
            <Input
              label="ID do Associado"
              type="number"
              value={associateId}
              onChange={(e) => setAssociateId(e.target.value)}
              placeholder="Digite o ID do associado"
              autoFocus
            />
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => setCurrentStep('select-partner')}
                className="flex-1"
              >
                Voltar
              </Button>
              <Button
                onClick={handleValidate}
                loading={loading}
                disabled={!associateId || loading}
                className="flex-1"
              >
                Validar
              </Button>
            </div>
          </div>
        </Card>
      </>
    );
  };

  const renderQRCode = () => {
    if (!validation) return null;

    return (
      <>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Confirma��o de Utiliza��o</h2>
          <p className="text-sm text-gray-600 mt-1">
            Verifique os dados e confirme a utiliza��o do benef�cio
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Dados do Associado</h3>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Nome</p>
                <p className="font-medium text-gray-900">{validation.associado.nome}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">CPF</p>
                <p className="font-medium text-gray-900">
                  {formatCPF(validation.associado.cpf)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <Badge variant="success">Ativo</Badge>
              </div>
            </div>
          </Card>

          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Dados do Parceiro</h3>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Estabelecimento</p>
                <p className="font-medium text-gray-900">{validation.parceiro.nome_fantasia}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Desconto Aplicado</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatPercentage(validation.desconto_percentual)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Data/Hora</p>
                <p className="font-medium text-gray-900">{formatDateTime(new Date())}</p>
              </div>
            </div>
          </Card>
        </div>

        <div className="flex gap-3 mt-6">
          <Button
            variant="secondary"
            onClick={handleReset}
            className="flex-1"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            loading={loading}
            disabled={loading}
            className="flex-1"
          >
            Confirmar Utiliza��o
          </Button>
        </div>
      </>
    );
  };

  const renderSuccess = () => (
    <>
      <div className="mb-6 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
          <svg
            className="w-8 h-8 text-green-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Utiliza��o Confirmada!</h2>
        <p className="text-sm text-gray-600 mt-1">
          O benef�cio foi utilizado com sucesso
        </p>
      </div>

      <Card className="text-center">
        <div className="space-y-6">
          <div>
            <p className="text-sm text-gray-600 mb-2">C�digo de Valida��o</p>
            <div className="inline-block p-4 bg-white border-2 border-gray-300 rounded-lg">
              <QRCode value={purchaseCode} size={200} />
            </div>
          </div>

          <div className="pt-4 border-t">
            <p className="text-lg font-mono font-bold text-gray-900">{purchaseCode}</p>
            <p className="text-xs text-gray-500 mt-1">Guarde este c�digo para refer�ncia</p>
          </div>

          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={() => navigate('/dashboard')}
              className="flex-1"
            >
              Ir para Dashboard
            </Button>
            <Button
              onClick={handleReset}
              className="flex-1"
            >
              Nova Utiliza��o
            </Button>
          </div>
        </div>
      </Card>
    </>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Nova Utiliza��o de Benef�cio</h1>
          <p className="mt-1 text-sm text-gray-600">
            Processo de utiliza��o do cart�o de benef�cios
          </p>
        </div>
        <Button variant="secondary" onClick={() => navigate('/dashboard')}>
          Cancelar
        </Button>
      </div>

      {error && (
        <Alert variant="error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <div className="mb-8">
        <div className="flex items-center justify-between">
          {['select-partner', 'validate', 'qrcode', 'success'].map((step, index) => {
            const stepLabels = {
              'select-partner': 'Selecionar Parceiro',
              'validate': 'Validar Associado',
              'qrcode': 'Confirma��o',
              'success': 'Conclu�do',
            };
            const isActive = currentStep === step;
            const isCompleted = ['select-partner', 'validate', 'qrcode', 'success'].indexOf(currentStep) > index;

            return (
              <div key={step} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isActive
                        ? 'bg-blue-600 text-white'
                        : isCompleted
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {isCompleted ? (
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      index + 1
                    )}
                  </div>
                  <span className="text-xs mt-2 text-center hidden sm:block">
                    {stepLabels[step as keyof typeof stepLabels]}
                  </span>
                </div>
                {index < 3 && (
                  <div
                    className={`h-1 flex-1 ${
                      isCompleted ? 'bg-green-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div>
        {currentStep === 'select-partner' && renderSelectPartner()}
        {currentStep === 'validate' && renderValidate()}
        {currentStep === 'qrcode' && renderQRCode()}
        {currentStep === 'success' && renderSuccess()}
      </div>
    </div>
  );
};

export default PurchaseFlow;
